import { useCallback } from 'react';
import { useAskMoo } from './useAskMoo';
import { parseContextType } from '../components/context-chips/ContextChip';
import type { ChatContext, DocumentContext } from '../types';

const PERSONAS =
  'admin|agency|carrier|cowbell|customer|da|mssp|pool|premiumprocessor|reinsurer';

const LIST_VIEW_PATTERNS: { pattern: RegExp; view: string }[] = [
  { pattern: new RegExp(`^/(?:${PERSONAS})/claims`), view: 'Claims' },
  // Endorsements is a sub-route of /policies — must precede the generic
  // policies rule so it resolves to 'Endorsements' not 'Policies'.
  {
    pattern: new RegExp(
      String.raw`^/(?:${PERSONAS})/(?:policies|policy)/endorsements(?:/|\?|$)`
    ),
    view: 'Endorsements',
  },
  {
    pattern: new RegExp(`^/(?:${PERSONAS})/(?:policies|policy)`),
    view: 'Policies',
  },
  { pattern: new RegExp(`^/(?:${PERSONAS})/submissions`), view: 'Submissions' },
  // Binders is a sub-route of /quotes — must precede the generic quotes rule.
  {
    pattern: new RegExp(String.raw`^/(?:${PERSONAS})/quotes/binders(?:/|\?|$)`),
    view: 'Binders',
  },
  { pattern: new RegExp(`^/(?:${PERSONAS})/quotes`), view: 'Quotes' },
  // Concierge / cmail email inbox — MUST precede the generic
  // ``accounts?`` pattern because that pattern lacks an end anchor
  // and would otherwise swallow ``/<persona>/accounts/<id>/cmail``
  // and resolve it as ``Accounts``. inferViewFromPath returns on the
  // first match, so the more-specific cmail patterns must win.
  { pattern: new RegExp(`^/(?:${PERSONAS})/cmail`), view: 'Concierge' },
  {
    pattern: new RegExp(`^/(?:${PERSONAS})/accounts?/[^/]+/cmail`),
    view: 'Concierge',
  },
  { pattern: new RegExp(`^/(?:${PERSONAS})/accounts?`), view: 'Accounts' },
  { pattern: new RegExp(`^/(?:${PERSONAS})/agencies`), view: 'Agencies' },
  { pattern: new RegExp(`^/(?:${PERSONAS})/binders`), view: 'Binders' },
  { pattern: new RegExp(`^/(?:${PERSONAS})/inbox`), view: 'UW Inbox' },
  {
    pattern: new RegExp(String.raw`^/(?:${PERSONAS})/dashboard(?:/|\?|$)`),
    view: 'Dashboard',
  },
  { pattern: /^\/agency\//, view: 'Broker' },
];

export function inferViewFromPath(pathname: string): string | undefined {
  for (const rule of LIST_VIEW_PATTERNS) {
    if (rule.pattern.test(pathname)) return rule.view;
  }
  return undefined;
}

interface UseChatContextsReturn {
  contexts: ChatContext[];
  addEntityContext: (
    entityType: string,
    entityId: string,
    entityData: Record<string, unknown>
  ) => void;
  /** Replace any existing entity of the same entityType with the new one. */
  replaceEntityContext: (
    entityType: string,
    entityId: string,
    entityData: Record<string, unknown>
  ) => void;
  addDocumentContext: (doc: Omit<DocumentContext, 'type'>) => void;
  addManualContext: (input: string) => void;
  removeContext: (index: number) => void;
  /**
   * Toggle whether a context chip is pinned. Pinned chips are kept in
   * in-memory state across navigation and `clearAttachments` (a "new chat"),
   * but are NOT persisted to storage — a page reload still drops them.
   */
  togglePinContext: (index: number) => void;
  clearContexts: () => void;
  clearAttachments: () => void;
}

export function useChatContexts(): UseChatContextsReturn {
  const {
    contexts,
    addContext,
    removeContext,
    clearContexts,
    clearAttachments,
    togglePinContext,
    setContexts,
  } = useAskMoo();

  const isDuplicate = useCallback(
    (entityType: string, entityId: string) =>
      contexts.some(
        (ctx) =>
          (ctx.type === 'entity' || ctx.type === 'screen') &&
          (ctx as { entityType: string; entityId: string }).entityType ===
            entityType &&
          (ctx as { entityType: string; entityId: string }).entityId ===
            entityId
      ),
    [contexts]
  );

  const addEntityContext = useCallback(
    (
      entityType: string,
      entityId: string,
      entityData: Record<string, unknown>
    ) => {
      if (isDuplicate(entityType, entityId)) return;
      addContext({
        type: 'entity',
        entityType: entityType as ChatContext extends { entityType: infer T }
          ? T
          : string,
        entityId,
        entityData,
      } as ChatContext);
    },
    [addContext, isDuplicate]
  );

  const replaceEntityContext = useCallback(
    (
      entityType: string,
      entityId: string,
      entityData: Record<string, unknown>
    ) => {
      if (isDuplicate(entityType, entityId)) return;
      setContexts((prev: ChatContext[]) => {
        // OMNI-188: A pinned chip of the same `entityType` represents an
        // explicit user choice — it must survive route-driven replacements
        // (e.g. expanding a different policy row in the listing). Only drop
        // *transient* (unpinned) chips of the same entityType.
        const filtered = prev.filter(
          (ctx) =>
            !(
              (ctx.type === 'entity' || ctx.type === 'screen') &&
              (ctx as { entityType: string }).entityType === entityType &&
              !(ctx as { pinned?: boolean }).pinned
            )
        );
        return [
          ...filtered,
          {
            type: 'entity',
            entityType,
            entityId,
            entityData,
          } as ChatContext,
        ];
      });
    },
    [isDuplicate, setContexts]
  );

  const addDocumentContext = useCallback(
    (doc: Omit<DocumentContext, 'type'>) => {
      addContext({ type: 'document', ...doc });
    },
    [addContext]
  );

  const addManualContext = useCallback(
    (input: string) => {
      const parsed = parseContextType(input);

      if (parsed) {
        if (isDuplicate(parsed.type, parsed.identifier)) return;
        addContext({
          type: 'entity',
          entityType: parsed.type,
          entityId: parsed.identifier,
          entityData: {},
        } as ChatContext);
      } else {
        addContext({
          type: 'selection',
          content: input,
          sourceView: '',
        });
      }
    },
    [addContext, isDuplicate]
  );

  return {
    contexts,
    addEntityContext,
    replaceEntityContext,
    addDocumentContext,
    addManualContext,
    removeContext,
    togglePinContext,
    clearContexts,
    clearAttachments,
  };
}
